import React, { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  faCircleCheck,
  faCircleMinus,
  faClipboardCheck,
  faClock,
  faSitemap,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons"
import type { IconProp } from "@fortawesome/fontawesome-svg-core"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import List, { type ListItem } from "../../components/List/List"
import { useChecklist } from "../../hooks/Checklist/useChecklist"
import type { ChecklistPendingItem } from "../../Objects/Checklist/ChecklistPendingItem"
import { ChecklistRunPopup } from "./ChecklistRunPopup"

const STATUS_STYLE: Record<ChecklistPendingItem["occurrenceStatus"], string> = {
  NotScheduledToday: "bg-slate-100 text-slate-600",
  UpToDate: "bg-green-100 text-green-700",
  PendingToday: "bg-amber-100 text-amber-700",
  Overdue: "bg-red-100 text-red-700",
}

const STATUS_ACCENT: Record<ChecklistPendingItem["occurrenceStatus"], string> = {
  NotScheduledToday: "#94a3b8",
  UpToDate: "#22c55e",
  PendingToday: "#f59e0b",
  Overdue: "#ef4444",
}

const STATUS_ICON: Record<ChecklistPendingItem["occurrenceStatus"], IconProp> = {
  NotScheduledToday: faCircleMinus,
  UpToDate: faCircleCheck,
  PendingToday: faClock,
  Overdue: faTriangleExclamation,
}

export const MyChecklistsPage: React.FC = () => {
  const { t } = useTranslation(["pages", "general"])

  const { getMyChecklists } = useChecklist()

  const [items, setItems] = useState<ChecklistPendingItem[]>([])

  const [running, setRunning] = useState<{ templateId?: number; executionId?: number } | null>(null)

  const load = useCallback(async () => {
    const result = await getMyChecklists()

    if (result) setItems(result.items)
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const listItems: ListItem[] = items.map((item, index) => ({
    id: item.templateId,
    index,
    title: item.templateName,
    linkImage: "",
    dataFilter: [item.templateName, item.sectorName ?? "", t(`checklists.my.status${item.occurrenceStatus}`)],
    children: (
      <div className="relative flex flex-col gap-3 p-4 pl-5 rounded-2xl overflow-hidden bg-white border border-gray-100 shadow-sm transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5">
        <div
          className="absolute left-0 top-0 bottom-0 w-1.5"
          style={{ backgroundColor: STATUS_ACCENT[item.occurrenceStatus] }}
        />

        <div className="flex items-start justify-between gap-3">
          <div className="flex gap-3 min-w-0 flex-1">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 text-white shadow-sm"
              style={{ backgroundColor: STATUS_ACCENT[item.occurrenceStatus] }}
            >
              <FontAwesomeIcon icon={faClipboardCheck} className="text-lg" />
            </div>

            <div className="flex flex-col justify-center min-w-0 flex-1">
              <span className="font-extrabold text-gray-900 text-lg truncate block">{item.templateName}</span>
              <span className="text-sm text-gray-500 font-medium truncate flex items-center gap-1.5">
                <FontAwesomeIcon icon={faSitemap} className="text-[10px]" />
                {item.sectorName ?? t("checklists.my.noSector")}
              </span>
            </div>
          </div>

          <span
            className={`px-3 py-1 text-xs font-bold rounded-full whitespace-nowrap flex items-center gap-1.5 ${STATUS_STYLE[item.occurrenceStatus]}`}
          >
            <FontAwesomeIcon icon={STATUS_ICON[item.occurrenceStatus]} />
            {t(`checklists.my.status${item.occurrenceStatus}`)}
          </span>
        </div>

        {item.deadlineTime && (
          <span className="inline-flex items-center gap-1.5 self-start px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold">
            <FontAwesomeIcon icon={faClock} />
            {t("checklists.my.deadline", { time: item.deadlineTime.slice(0, 5) })}
          </span>
        )}
      </div>
    ),
  }))

  return (
    <div className={defaultStyle.containerList}>
      <List
        enableLocalPagination
        showFilterStatus={false}
        messageCount={t("checklists.my.listCount")}
        messageEmpty={t("checklists.my.empty")}
        nameFindSearch={t("checklists.my.searchPlaceholder")}
        data={listItems}
        handleRefreshButton={load}
        handleOnClick={(id) => {
          const item = items.find((e) => e.templateId === id)

          if (!item) return

          setRunning(
            item.inProgressExecutionId ? { executionId: item.inProgressExecutionId } : { templateId: item.templateId },
          )
        }}
      />

      {running && (
        <ChecklistRunPopup
          templateId={running.templateId}
          executionId={running.executionId}
          onClose={() => setRunning(null)}
          onChanged={load}
        />
      )}
    </div>
  )
}
